import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';

interface TimeTracking {
  totalSeconds: number;
  isRunning: boolean;
  startTime?: string;
}

interface TimeTrackingState {
  [taskId: string]: TimeTracking;
}

interface TimeTrackingContextType {
  startTimer: (taskId: string) => void;
  pauseTimer: (taskId: string) => void;
  resetTimer: (taskId: string) => void;
  getTaskTime: (taskId: string) => TimeTracking;
  setTaskTime: (taskId: string, totalSeconds: number) => void;
}

const TimeTrackingContext = createContext<TimeTrackingContextType | null>(null);

const STORAGE_KEY = 'taskTimeTracking';
const ACTIVE_TABS_KEY = 'timeTrackingActiveTabs';

export const TimeTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Generate a unique ID for this tab
  const tabId = React.useRef(Math.random().toString(36).substring(2, 15));

  // Initialize active tabs tracking
  useEffect(() => {
    try {
      // Add this tab to active tabs
      const activeTabs = JSON.parse(safeLocalStorage.getItem(ACTIVE_TABS_KEY) || '[]');
      activeTabs.push(tabId.current);
      safeLocalStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(activeTabs));

      // Remove this tab when it's closed
      const handleUnload = () => {
        const currentTabs = JSON.parse(safeLocalStorage.getItem(ACTIVE_TABS_KEY) || '[]');
        const remainingTabs = currentTabs.filter((id: string) => id !== tabId.current);
        safeLocalStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(remainingTabs));

        // If this is the last tab, pause all timers
        if (remainingTabs.length === 0) {
          const currentTimers = JSON.parse(safeLocalStorage.getItem(STORAGE_KEY) || '{}');
          const updatedTimers = Object.entries(currentTimers).reduce((acc, [id, timer]: [string, any]) => {
            if (timer.isRunning) {
              const startTime = timer.startTime ? new Date(timer.startTime).getTime() : 0;
              const currentTime = new Date().getTime();
              const additionalSeconds = Math.floor((currentTime - startTime) / 1000);
              
              acc[id] = {
                ...timer,
                totalSeconds: timer.totalSeconds + additionalSeconds,
                isRunning: false,
                startTime: undefined
              };
            } else {
              acc[id] = timer;
            }
            return acc;
          }, {});
          
          safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTimers));
        }
      };

      window.addEventListener('beforeunload', handleUnload);
      return () => window.removeEventListener('beforeunload', handleUnload);
    } catch (error) {
      console.error('Error managing active tabs:', error);
    }
  }, []);
  const [timers, setTimers] = useState<TimeTrackingState>(() => {
    try {
      const saved = safeLocalStorage.getItem(STORAGE_KEY);
      if (!saved) return {};

      const savedTimers = JSON.parse(saved);

      // Check if there are any active tabs
      const activeTabs = JSON.parse(safeLocalStorage.getItem(ACTIVE_TABS_KEY) || '[]');
      
      // If there are no active tabs (first tab being opened), ensure all timers are paused
      if (activeTabs.length === 0) {
        const initialTimers: TimeTrackingState = {};
        Object.entries(savedTimers).forEach(([taskId, timer]: [string, TimeTracking]) => {
          initialTimers[taskId] = {
            ...timer,
            isRunning: false,
            startTime: undefined
          };
        });
        return initialTimers;
      }

      // Otherwise, keep the current state
      return savedTimers;
    } catch {
      return {};
    }
  });

  // Handle tab/browser closure
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Pause all running timers and save their current state
      const updatedTimers = { ...timers };
      let hasUpdates = false;

      Object.entries(timers).forEach(([taskId, timer]) => {
        if (timer.isRunning) {
          const startTime = timer.startTime ? new Date(timer.startTime).getTime() : 0;
          const currentTime = new Date().getTime();
          const additionalSeconds = Math.floor((currentTime - startTime) / 1000);

          updatedTimers[taskId] = {
            totalSeconds: timer.totalSeconds + additionalSeconds,
            isRunning: false,
            startTime: undefined
          };
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTimers));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [timers]);

  // Save timers to localStorage whenever they change
  useEffect(() => {
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
  }, [timers]);

  // Update running timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(currentTimers => {
        const updatedTimers = { ...currentTimers };
        let hasUpdates = false;

        Object.entries(currentTimers).forEach(([taskId, timer]) => {
          if (timer.isRunning && timer.startTime) {
            const startTime = new Date(timer.startTime).getTime();
            const currentTime = new Date().getTime();
            const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
            
            if (elapsedSeconds > 0) {
              updatedTimers[taskId] = {
                ...timer,
                totalSeconds: timer.totalSeconds + elapsedSeconds,
                startTime: new Date().toISOString()
              };
              hasUpdates = true;
            }
          }
        });

        return hasUpdates ? updatedTimers : currentTimers;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const startTimer = useCallback((taskId: string) => {
    setTimers(prev => ({
      ...prev,
      [taskId]: {
        totalSeconds: (prev[taskId]?.totalSeconds || 0),
        isRunning: true,
        startTime: new Date().toISOString()
      }
    }));
  }, []);

  const pauseTimer = useCallback((taskId: string) => {
    setTimers(prev => {
      if (!prev[taskId]) return prev;

      const timer = prev[taskId];
      let totalSeconds = timer.totalSeconds;

      // Add any additional seconds since the last update
      if (timer.isRunning && timer.startTime) {
        const startTime = new Date(timer.startTime).getTime();
        const currentTime = new Date().getTime();
        const additionalSeconds = Math.floor((currentTime - startTime) / 1000);
        totalSeconds += additionalSeconds;
      }

      return {
        ...prev,
        [taskId]: {
          totalSeconds,
          isRunning: false,
          startTime: undefined
        }
      };
    });
  }, []);

  const resetTimer = useCallback((taskId: string) => {
    setTimers(prev => ({
      ...prev,
      [taskId]: {
        totalSeconds: 0,
        isRunning: false,
        startTime: undefined
      }
    }));
  }, []);

  const getTaskTime = useCallback((taskId: string): TimeTracking => {
    const timer = timers[taskId];
    if (!timer) {
      return {
        totalSeconds: 0,
        isRunning: false
      };
    }

    if (!timer.isRunning) {
      return timer;
    }

    // Calculate current total including time since last update
    if (timer.startTime) {
      const startTime = new Date(timer.startTime).getTime();
      const currentTime = new Date().getTime();
      const additionalSeconds = Math.floor((currentTime - startTime) / 1000);
      return {
        ...timer,
        totalSeconds: timer.totalSeconds + additionalSeconds
      };
    }

    return timer;
  }, [timers]);

  const setTaskTime = useCallback((taskId: string, totalSeconds: number) => {
    setTimers(prev => ({
      ...prev,
      [taskId]: {
        totalSeconds,
        isRunning: false,
        startTime: undefined
      }
    }));
  }, []);

  const value = {
    startTimer,
    pauseTimer,
    resetTimer,
    getTaskTime,
    setTaskTime
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  }
  return context;
};